import React, { useState, useEffect, useRef } from 'react';
import {
  Star,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { useSiteContext } from '../contexts/SiteContext';

// Currency configuration
const CURRENCY = '£';

const ProductAnalytics = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [topPerformers, setTopPerformers] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last_7_days');
  const [topLimit, setTopLimit] = useState(10);
  const [sortBy, setSortBy] = useState('qty_sold');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOrders, setProductOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const ordersRef = useRef(null);

  // Comparison mode state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [weeksToCompare, setWeeksToCompare] = useState(4);
  const [comparisonData, setComparisonData] = useState(null);
  const [droppedProducts, setDroppedProducts] = useState([]);

  useEffect(() => {
    if (!selectedSite) return;
    if (comparisonMode) {
      fetchComparisonData();
    } else {
      fetchTopPerformers();
    }
  }, [user, dateRange, topLimit, sortBy, selectedSite, comparisonMode, weeksToCompare]);


  const fetchTopPerformers = async () => {
    setPerformanceLoading(true);
    try {
      const params = new URLSearchParams({
        dateRange,
        limit: topLimit,
        siteId: selectedSite?.id || '',
        category
      });
      const response = await fetch(`/app/api/analytics/product-performance.php?${params}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let performers = data.topPerformers || [];

        // Sort based on sortBy selection
        performers.sort((a, b) => {
          if (sortBy === 'qty_sold') {
            return b.qty_sold - a.qty_sold;
          } else if (sortBy === 'orders_count') {
            return b.orders_count - a.orders_count;
          }
          return 0;
        });

        setTopPerformers(performers);
      }
    } catch (error) {
      console.error('Error fetching top performers:', error);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const fetchComparisonData = async () => {
    setPerformanceLoading(true);
    try {
      const params = new URLSearchParams({
        comparisonMode: 'weekly',
        weeks: weeksToCompare,
        limit: topLimit,
        siteId: selectedSite?.id || '',
        category
      });
      const response = await fetch(`/app/api/analytics/product-performance.php?${params}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let performers = data.topPerformers || [];

        // Sort based on sortBy selection
        performers.sort((a, b) => {
          if (sortBy === 'qty_sold') {
            return b.currentWeekQty - a.currentWeekQty;
          } else if (sortBy === 'orders_count') {
            const aOrders = a.weeklyData?.[a.weeklyData.length - 1]?.orders_count || 0;
            const bOrders = b.weeklyData?.[b.weeklyData.length - 1]?.orders_count || 0;
            return bOrders - aOrders;
          }
          return 0;
        });

        setTopPerformers(performers);
        setComparisonData(data.comparisonData || null);
        setDroppedProducts(data.droppedProducts || []);
      }
    } catch (error) {
      console.error('Error fetching comparison data:', error);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const fetchProductOrders = async (sku, productName) => {
    setOrdersLoading(true);
    setSelectedProduct({ sku, name: productName });
    try {
      const params = new URLSearchParams({
        sku,
        dateRange,
        siteId: selectedSite?.id || ''
      });
      const response = await fetch(`/app/api/analytics/product-orders.php?${params}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProductOrders(data.orders || []);

        // Scroll to orders section after data is loaded
        setTimeout(() => {
          if (ordersRef.current) {
            ordersRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching product orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">
            {selectedSite ? `Top performing products for ${selectedSite.name}` : 'Identify your top selling products across all sites'}
          </p>
        </div>
        <button
          onClick={() => setComparisonMode(!comparisonMode)}
          className={`relative px-6 py-3 rounded-lg text-base font-semibold transition-all duration-300 ${
            comparisonMode
              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
              : 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 shadow-xl hover:shadow-2xl animate-pulse hover:animate-none ring-4 ring-purple-300 ring-opacity-50'
          }`}
        >
          {!comparisonMode && (
            <span className="absolute -top-3 -right-3 flex h-7 w-7">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-7 w-7 bg-yellow-400 text-sm items-center justify-center font-bold text-yellow-900 shadow-lg">!</span>
            </span>
          )}
          {comparisonMode ? 'Standard View' : 'Compare Periods'}
        </button>
      </div>

      {/* Dropped Products Alert - Only in Comparison Mode */}
      {comparisonMode && droppedProducts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-amber-800 font-medium">
                Products Dropped from Top {topLimit}
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                These products were previously in your top sellers but are no longer in the current week's rankings.
              </p>
              <div className="mt-3 space-y-2">
                {droppedProducts.map((product) => (
                  <div
                    key={product.sku}
                    className="flex flex-wrap justify-between items-center bg-white rounded-md px-3 py-2 border border-amber-100"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{product.name}</span>
                      <span className="text-xs text-gray-500">({product.sku})</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-amber-700">
                        Was #{product.lastSeenRank} for {product.weeksInTop} week{product.weeksInTop > 1 ? 's' : ''}
                      </span>
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                        Missing {product.weeksMissing} week{product.weeksMissing > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Performers Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {comparisonMode
                ? `Top Performing Products - ${weeksToCompare} Week Comparison`
                : 'Top Performing Products'}
            </h3>
            <div className="flex gap-3">
              {comparisonMode ? (
                <select
                  value={weeksToCompare}
                  onChange={(e) => setWeeksToCompare(parseInt(e.target.value))}
                  className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="2">2 Weeks</option>
                  <option value="4">4 Weeks</option>
                  <option value="6">6 Weeks</option>
                  <option value="8">8 Weeks</option>
                  <option value="12">12 Weeks</option>
                </select>
              ) : (
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_year">This Year</option>
                  <option value="last_year">Last Year</option>
                </select>
              )}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="qty_sold">By Quantity Sold</option>
                <option value="orders_count">By Number of Orders</option>
              </select>
              <select
                value={topLimit}
                onChange={(e) => setTopLimit(parseInt(e.target.value))}
                className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="5">Top 5</option>
                <option value="10">Top 10</option>
                <option value="20">Top 20</option>
              </select>
            </div>
          </div>

          {performanceLoading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : topPerformers.length > 0 ? (
            comparisonMode ? (
              /* Comparison Mode Table */
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trend
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        This Week
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        vs Last Week
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {weeksToCompare}wk Total
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topPerformers.map((product, index) => (
                      <tr key={product.sku} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                            index === 0 ? 'bg-yellow-100 text-yellow-800' :
                            index === 1 ? 'bg-gray-100 text-gray-800' :
                            index === 2 ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-50 text-blue-600'
                          }`}>
                            <span className="text-sm font-semibold">#{index + 1}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer max-w-xs truncate"
                              onClick={() => fetchProductOrders(product.sku, product.name)}
                            >
                              {product.name}
                            </div>
                            {/* Rank trend badge */}
                            {product.rankTrend === 'new' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded font-medium flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> New
                              </span>
                            )}
                            {product.rankTrend === 'improving' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded font-medium flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" /> Rising
                              </span>
                            )}
                            {product.rankTrend === 'declining' && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded font-medium flex items-center gap-1">
                                <TrendingDown className="w-3 h-3" /> Declining
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{product.sku}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {product.weeklyData && product.weeklyData.length > 0 && (
                            <ResponsiveContainer width={80} height={30}>
                              <LineChart data={product.weeklyData}>
                                <Line
                                  type="monotone"
                                  dataKey="qty_sold"
                                  stroke={product.delta >= 0 ? '#10b981' : '#ef4444'}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {Math.round(product.currentWeekQty)} units
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className={`flex items-center gap-1 text-sm font-medium ${
                            product.delta > 0 ? 'text-green-600' : product.delta < 0 ? 'text-red-600' : 'text-gray-500'
                          }`}>
                            {product.delta > 0 ? (
                              <ArrowUpRight className="w-4 h-4" />
                            ) : product.delta < 0 ? (
                              <ArrowDownRight className="w-4 h-4" />
                            ) : null}
                            {product.delta > 0 ? '+' : ''}{Math.round(product.delta)}
                            {product.deltaPercent !== 0 && (
                              <span className="text-xs text-gray-500">
                                ({product.deltaPercent > 0 ? '+' : ''}{product.deltaPercent}%)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-700">
                            {Math.round(product.totalQty)} units
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-green-600">
                            {CURRENCY}{product.totalRevenue?.toFixed(2) || '0.00'}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Standard View Table */
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty Sold
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Orders
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topPerformers.map((product, index) => (
                      <tr key={product.sku} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                            index === 0 ? 'bg-yellow-100 text-yellow-800' :
                            index === 1 ? 'bg-gray-100 text-gray-800' :
                            index === 2 ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-50 text-blue-600'
                          }`}>
                            <span className="text-sm font-semibold">#{index + 1}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer max-w-xs truncate"
                            onClick={() => fetchProductOrders(product.sku, product.name)}
                          >
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500">{product.site_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.sku}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className="text-sm font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                            onClick={() => fetchProductOrders(product.sku, product.name)}
                          >
                            {Math.round(product.qty_sold)} units
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-green-600">
                            {CURRENCY}{product.revenue.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.orders_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className="text-center py-8">
              <Star className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No sales data found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No completed orders found for the selected {comparisonMode ? 'period' : 'date range'}.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Product Orders Section */}
      {selectedProduct && (
        <div ref={ordersRef} className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Orders in selected date range for "{selectedProduct.name}"
              </h3>
              <button
                onClick={() => setSelectedProduct(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            {ordersLoading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : productOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Qty
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Items
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Site
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {productOrders.map((order, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{order.order_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(order.order_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.customer_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {order.product_qty}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.total_items}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {CURRENCY}{order.order_total.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.site_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Star className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No orders found for this product in the selected date range.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAnalytics;